/**
 * First Session Free UI (Sam, Oct 3 2026). Plain English copy on purpose
 * (no en.json keys) so it can't collide with other workers' copy edits.
 * Every block has a next step; nothing here is a dead end.
 */
import React, { useContext, useEffect, useState } from 'react';
import { ReactReduxContext } from 'react-redux';
import { useLocation } from 'react-router-dom';

import NamedLink from '../NamedLink/NamedLink';
import { useGrowthStatus } from '../GrowthPromos/GrowthPromos';
import { HelpScoutBeaconSuppressor } from '../HelpScoutBeacon/HelpScoutBeacon';
import {
  useFirstFreeStatus,
  talkerEligible,
  talkerNeedsVerify,
  listenerPromoLive,
  offerLive,
  isFirstSessionFreeTx,
  listenerPayoutLabelOf,
  popupAlreadyShown,
  markPopupShown,
  FREE_PRICES,
  FORMAT_WORDS,
  rewardOf,
  formatWithinReward,
  FREE_FORMATS,
  isSweetTreatTx,
  SWEET_TREAT_LINE,
  SWEET_TREAT_BADGE,
  SWEET_TREAT_LISTENER_LABEL,
  SWEET_TREAT_TALKER_LABEL,
} from '../../util/promoFirstFree';
import { getAccountRole, ACCOUNT_ROLE_TALK, ACCOUNT_ROLE_BOTH } from '../../util/accountRole';
import { fetchFirstFreeTx, fetchFirstFreeStatus } from '../../util/api';

import css from './FirstSessionFree.module.css';

// ------------------------------------------- listener-funded model (Oct 13) --
// Public list: master switch + listeners who opted out. Badges use it.
let offersCache = { at: 0, data: null, promise: null };
const offersSubs = new Set();
const loadPublicOffers = (force = false) => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return Promise.resolve(null);
  if (!force && offersCache.data && Date.now() - offersCache.at < 60 * 1000) return Promise.resolve(offersCache.data);
  if (!force && offersCache.promise) return offersCache.promise;
  offersCache.promise = window
    .fetch('/api/promo/first-free/public', { credentials: 'include', headers: { Accept: 'application/json' } })
    .then(r => (r.ok ? r.json() : null))
    .then(j => {
      offersCache = { at: Date.now(), data: j?.data || null, promise: null };
      offersSubs.forEach(fn => fn(offersCache.data));
      return offersCache.data;
    })
    .catch(() => {
      offersCache = { at: Date.now(), data: null, promise: null };
      return null;
    });
  return offersCache.promise;
};
export const usePublicOffers = () => {
  const [data, setData] = useState(offersCache.data);
  useEffect(() => {
    let ok = true;
    const fn = d => ok && setData(d);
    offersSubs.add(fn);
    loadPublicOffers().then(fn);
    return () => {
      ok = false;
      offersSubs.delete(fn);
    };
  }, []);
  return data;
};
/**
 * Sweet Treat badge rule (Oct 19 2026): program on, listener hasn't turned it off,
 * their start date has passed (own setup date, or the existing-listener date after
 * the notice email), and they're under their weekly limit.
 */
export const listenerOffersFree = (offers, listenerId, nowMs = Date.now()) => {
  if (!offers?.live || !listenerId) return false;
  if ((offers.optedOut || []).includes(listenerId)) return false;
  if ((offers.cappedIds || []).includes(listenerId)) return false;
  if ((offers.startedIds || []).includes(listenerId)) return true;
  return !!(offers.existingStartsAt && offers.existingStartsAt <= nowMs);
};
const isListenerFundedStatus = status => status?.offer?.funding === 'listener';


export const GiftIcon = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <rect x="7" y="19" width="34" height="23" rx="4" fill="#F51F79" />
    <rect x="5" y="13" width="38" height="9" rx="3" fill="#FF5FA2" />
    <rect x="21" y="13" width="6" height="29" fill="#159BE8" />
    <path
      d="M24 13c-3-6-11-8-12-3-1 4 6 4 12 3zm0 0c3-6 11-8 12-3 1 4-6 4-12 3z"
      fill="none"
      stroke="#159BE8"
      strokeWidth="3"
      strokeLinejoin="round"
    />
  </svg>
);

const SparkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2l2.4 6.9L21 11l-6.6 2.1L12 20l-2.4-6.9L3 11l6.6-2.1z" fill="currentColor" />
  </svg>
);

/** "First session free" pill for listener cards / profiles / booking panel. */
export const FirstSessionFreeBadge = ({ className, label = SWEET_TREAT_BADGE, noIcon, ...rest }) => (
  <span className={className ? `${css.badge} ${className}` : css.badge} {...rest}>
    {noIcon ? null : <SparkIcon />}
    {label}
  </span>
);

// Works with or without a Redux <Provider> (unit tests render bare components).
const useSafeSelector = selector => {
  const ctx = useContext(ReactReduxContext);
  const store = ctx?.store || null;
  const [value, setValue] = useState(() => (store ? selector(store.getState()) : null));
  useEffect(() => {
    if (!store) return undefined;
    const run = () => setValue(selector(store.getState()));
    run();
    return store.subscribe(run);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);
  return value;
};
const selectCurrentUser = state => state?.user?.currentUser || null;
const selectIsAuthenticated = state => !!state?.auth?.isAuthenticated;
const useCurrentUser = () => useSafeSelector(selectCurrentUser);
const useIsAuthenticated = () => useSafeSelector(selectIsAuthenticated);

/**
 * Hook for other components: { status, eligible, live, needsVerify }.
 */
export const useFirstSessionFree = currentUserProp => {
  const fromStore = useCurrentUser();
  const currentUser = currentUserProp !== undefined ? currentUserProp : fromStore;
  const status = useFirstFreeStatus(currentUser);
  // Sam 11:54 PM Oct 23: once checkout refused the free session (card/ID already had one),
  // hide every free reference and skip the Yes/No prompt for this account.
  // Oct 24: server-sourced too (status.talker.reason), so a fresh browser knows why.
  const serverRefused = ['card_used', 'id_used', 'already_claimed', 'already_booked'].includes(status?.talker?.reason)
    ? status.talker.reason
    : null;
  const refused = !!serverRefused || freeRefused(currentUser?.id?.uuid);
  const refusedReason = serverRefused || (refused ? 'card_used' : null);
  return {
    status,
    live: offerLive(status),
    eligible: !refused && talkerEligible(status),
    needsVerify: !refused && talkerNeedsVerify(status),
    listenerLive: listenerPromoLive(status),
    guest: !currentUser?.id,
    refusedReason,
  };
};

// Pages where the pop-up must never interrupt: sessions / calls / checkout /
// auth / verification / ops.
const POPUP_BLOCKED_RE = /^\/(order|sale|session|login|signup|confirm|choose-role|recover-password|reset-password|verify-email|account\/identity|ops|styleguide|preview)(\/|$)|\/(checkout|book)(\/|$)/;

export const popupAllowedOnPath = pathname => !POPUP_BLOCKED_RE.test(pathname || '/');

/**
 * Branded login pop-up. Shows once per login session, only while the promo
 * switch is on and the talker is still eligible. Never for listeners.
 */
export const FirstSessionFreePopup = () => {
  const location = useLocation();
  const currentUser = useCurrentUser();
  const isAuthenticated = useIsAuthenticated();
  const userId = currentUser?.id?.uuid || null;
  // Talker and 'both' accounts (Sam, Oct 3); never listener-only accounts.
  const accountRole = getAccountRole(currentUser);
  const isTalkerOnly = accountRole === ACCOUNT_ROLE_TALK || accountRole === ACCOUNT_ROLE_BOTH;
  const status = useFirstFreeStatus(isAuthenticated && userId ? currentUser : null);
  const [open, setOpen] = useState(false);

  const allowedPath = popupAllowedOnPath(location.pathname);
  const eligible =
    isAuthenticated && !!userId && isTalkerOnly && status?.role === 'talker' && talkerEligible(status);

  useEffect(() => {
    if (!eligible || !allowedPath || open) {
      return undefined;
    }
    if (popupAlreadyShown(userId)) {
      return undefined;
    }
    // Let the page paint first so it doesn't feel like a wall.
    const t = window.setTimeout(() => {
      markPopupShown(userId);
      setOpen(true);
    }, 900);
    return () => window.clearTimeout(t);
  }, [eligible, allowedPath, userId, open]);

  // Navigating into a blocked page (e.g. a session) closes it.
  useEffect(() => {
    if (open && !allowedPath) setOpen(false);
  }, [open, allowedPath]);

  useEffect(() => {
    if (!open) return undefined;
    try {
      const el = document.querySelector('[data-testid="first-free-popup"] a');
      if (el) el.focus();
    } catch (e) {
      // ignore
    }
    const onKey = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) {
    return null;
  }
  const close = () => setOpen(false);
  return (
    <div
      className={css.overlay}
      role="presentation"
      onClick={e => {
        if (e.target === e.currentTarget) close();
      }}
      data-testid="first-free-popup"
    >
      <HelpScoutBeaconSuppressor reason="first-free-popup" />
      <div
        className={css.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="firstFreePopupTitle"
      >
        <div className={css.dialogHero}>
          <button type="button" className={css.close} onClick={close} aria-label="Close">
            ×
          </button>
          <span className={css.giftWrap}>
            <GiftIcon size={46} />
          </span>
          <div>
            <span className={css.pill}>Free session</span>
          </div>
        </div>
        <div className={css.dialogBody}>
          <h2 id="firstFreePopupTitle" className={css.dialogTitle}>
            Your first 15 minutes are <em>free</em>
          </h2>
          <p className={css.dialogText}>
            Talk to a caring listener by chat, voice or video. Your first 15 minutes are free.
          </p>
          <ul className={css.facts}>
            <li className={css.fact}>Chat, voice or video</li>
            <li className={css.fact}>15 minutes</li>
            <li className={css.fact}>$0 to you</li>
          </ul>
          <div className={css.actions}>
            <NamedLink
              name="SearchPage"
              className={css.pinkButton}
              onClick={close}
            >
              Find someone to talk to or learn from
            </NamedLink>
            <button type="button" className={css.later} onClick={close}>
              Maybe later
            </button>
          </div>
          <p className={css.fine}>
            One free session per person. A card is needed to book, but you won't be charged.
            Video sessions aren't included.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Banner for the homepage / search (guests + eligible talkers) and the talker
 * dashboard ("Verify your ID to claim" for unverified talkers).
 */
export const FirstSessionFreeBanner = ({ placement = 'home', className }) => {
  const { status, live, eligible, needsVerify, guest } = useFirstSessionFree();
  if (!live) {
    return null;
  }
  if (!guest && status?.role !== 'talker') {
    return null;
  }
  const claim = status?.talker?.claim;
  // Sam 7:51 AM: no ID for talkers/learners, so no verify-ID free-session card.
  if (!guest && (needsVerify || !eligible)) {
    return null;
  }
  const funded = isListenerFundedStatus(status);
  let title = funded ? 'Sweet Treat: your first session is free' : 'Your free session is ready';
  let sub = funded
    ? `${SWEET_TREAT_LINE} One free 15-minute session per person.`
    : `You earned one free 15-minute ${rewardWords(rewardOf(status))} session. Pick any listener. Use it by Oct 31.`;
  let cta = (
    <NamedLink name="SearchPage" className={`${css.pinkButton} ${css.bannerCta}`}>
      Find someone to talk to or learn from
    </NamedLink>
  );
  if (guest) {
    sub = funded
      ? `${SWEET_TREAT_LINE} Sign up to use it.`
      : 'Sign up and your first chat or voice session is free, while spots last.';
    cta = (
      <NamedLink name="SignupPage" className={`${css.pinkButton} ${css.bannerCta}`}>
        Sign up free
      </NamedLink>
    );
  } else if (needsVerify) {
    title = 'Verify your ID to use your free session';
    sub = funded
      ? 'Your Sweet Treat (a free first 15-minute session, any format) is waiting. Verifying takes about 2 minutes.'
      : 'Your free 15-minute chat, voice or video session is waiting. It takes about 2 minutes. Use it by Oct 31.';
    cta = (
      <NamedLink
        name="YourProfilePage"
        to={{ search: '?tab=talking', hash: 'identity' }}
        className={`${css.pinkButton} ${css.bannerCta}`}
      >
        Verify your ID
      </NamedLink>
    );
  } else if (placement === 'dashboard') {
    // Talker dashboard reminder (Sam, Oct 21 2026).
    title = 'You have a free first session to use';
    sub = 'Someone warm and caring is ready to hear you. Pick a listener with the 🎁 badge, and your first 15 minutes are free.';
    cta = (
      <NamedLink
        name="SearchPage"
        to={{ search: '?mode=listeners&sweetTreat=1' }}
        className={`${css.pinkButton} ${css.bannerCta}`}
        data-testid="first-free-dashboard-cta"
      >
        Browse free-session listeners
      </NamedLink>
    );
  } else if (placement === 'search') {
    sub = funded
      ? `${SWEET_TREAT_LINE} Tap the “Sweet Treat” filter. The payment page shows $0.00.`
      : 'Pick any listener for chat, voice or video. The payment page shows $0.00.';
    cta = null;
  }
  if (claim) {
    return null;
  }
  return (
    <section
      className={className ? `${css.banner} ${className}` : css.banner}
      data-testid={`first-free-banner-${placement}`}
    >
      <span className={css.bannerIcon}>
        <GiftIcon size={30} />
      </span>
      <div className={css.bannerText}>
        <span className={css.pill}>{funded ? 'Sweet Treat' : 'Free session'}</span>
        <p className={css.bannerTitle}>{title}</p>
        <p className={css.bannerSub}>{sub}</p>
      </div>
      {cta}
    </section>
  );
};

/** Checkout: the price line the talker sees before anything is requested. */
export const FirstSessionFreeCheckoutNote = ({ sessionFormat, priceLabel }) => {
  const price = priceLabel || FREE_PRICES[sessionFormat] || '$20';
  const funded = isListenerFundedStatus(useFirstSessionFree().status);
  return (
    <div className={css.checkoutNote} data-testid="first-free-checkout-note">
      <span className={css.pill}>{funded ? SWEET_TREAT_TALKER_LABEL : 'Free session'}</span>
      <div className={css.lineRow}>
        <span>15-minute {sessionFormat === 'chat' ? 'chat' : sessionFormat === 'video' ? 'video' : 'voice'} session</span>
        <span className={css.lineAmount}>{price}.00</span>
      </div>
      <div className={`${css.lineRow} ${css.lineRowMuted}`}>
        <span>{funded ? 'Sweet Treat: your first 15 minutes are free' : 'Free session, covered by us'}</span>
        <span className={css.lineAmountGreen}>−{price}.00</span>
      </div>
      <div className={css.totalRow}>
        <span>Total</span>
        <span>$0.00</span>
      </div>
      <p className={css.noteSmall}>
        You won't be charged for this session. It's your one free first session (any format),
        15 minutes, and can't be extended. Your next sessions are at the normal price.
      </p>
    </div>
  );
};

/** Checkout: the free spot went while the talker was booking. */
export const FreeSpotGoneNotice = ({ reason, nextMonth, onBookPaid, sessionFormat, backTo, priceLabel }) => {
  // Sweet Skillz: skill sessions pass the listener's own price label.
  const price = priceLabel || FREE_PRICES[sessionFormat] || null;
  if (reason === 'expired') {
    return (
      <div className={css.gone} role="alert" data-testid="first-free-gone">
        <p className={css.goneTitle}>Your Sweet Treat had to be used within 30 days of signing up</p>
        <p className={css.noteSmall}>You can still book at the normal price. Your card wasn't charged.</p>
        {onBookPaid ? (
          <button type="button" className={css.pinkButton} onClick={onBookPaid}>
            Book at the normal price
          </button>
        ) : null}
      </div>
    );
  }
  if (reason === 'format_over_value' || reason === 'too_early' || reason === 'locked') {
    return (
      <div className={css.gone} role="alert" data-testid="first-free-gone">
        <p className={css.goneTitle}>
          {reason === 'format_over_value'
            ? 'Your free session covers the same type you booked, or a cheaper one'
            : 'Your free session unlocks Oct 19'}
        </p>
        <p className={css.noteSmall}>
          {reason === 'format_over_value'
            ? 'Pick that format (or a cheaper one) for free, or book this one at the normal price.'
            : 'Pick a time from Oct 19 to Oct 31 for your free session, or book now at the normal price.'}{' '}
          Your card wasn't charged.
        </p>
        {onBookPaid ? (
          <button type="button" className={css.pinkButton} onClick={onBookPaid}>
            Book at the normal price
          </button>
        ) : null}
      </div>
    );
  }
  const already = reason === 'already_claimed' || reason === 'already_booked';
  const onePerPerson = reason === 'card_used' || reason === 'id_used' || reason === 'self_dealing';
  const title = already
    ? "You've already used your free first session"
    : onePerPerson
    ? "The free first session isn't available here"
    : reason === 'listener_cap'
    ? 'This listener has given all their Sweet Treats this week'
    : reason === 'listener_opted_out'
    ? "This listener doesn't offer the Sweet Treat right now"
    : "Sorry, free sessions aren't available right now";
  const text = already
    ? 'You can still book this listener at the normal price.'
    : onePerPerson
    ? 'Free sessions are one per person and one per card, and this card or ID has already had one. You can still book at the normal price.'
    : reason === 'listener_cap' || reason === 'listener_opted_out'
    ? 'Pick another listener with the Sweet Treat badge for your free session, or book this listener at the normal price.'
    : `You can book at the normal price now${nextMonth ? `, or come back on ${nextMonth}` : ''}.`;
  return (
    <div className={css.gone} role="alert" data-testid="first-free-gone">
      <p className={css.goneTitle}>{title}</p>
      <p className={css.noteSmall}>{text} Your card wasn't charged.</p>
      <div className={css.goneActions}>
        <button type="button" className={css.pinkButton} onClick={onBookPaid}>
          {price ? `Book at normal price (${price})` : 'Book at normal price'}
        </button>
        <NamedLink name="SearchPage" className={css.blueLink}>
          Find another listener
        </NamedLink>
        {backTo ? (
          <NamedLink {...backTo} className={css.blueLink}>
            ← Back
          </NamedLink>
        ) : null}
      </div>
    </div>
  );
};

const REFUSED_KEY = id => `sl_free_refused:${id}`;
export const freeRefused = userId => {
  try {
    const at = userId && Number(window.localStorage.getItem(REFUSED_KEY(userId)));
    return !!at && Date.now() - at < 30 * 24 * 3600 * 1000;
  } catch (e) {
    return false;
  }
};
export const markFreeRefused = userId => {
  try {
    if (userId) window.localStorage.setItem(REFUSED_KEY(userId), String(Date.now()));
  } catch (e) {}
};

export const promoErrorReason = err => {
  const code =
    err?.data?.data?.code ||
    err?.data?.code ||
    err?.code ||
    (typeof err?.statusText === 'string' ? err.statusText : '');
  return typeof code === 'string' && code.indexOf('promo_') === 0 ? code.slice(6) : null;
};

const money = subunits => (Number.isFinite(subunits) ? `$${(subunits / 100).toFixed(2)}` : null);

/**
 * Transaction page / inbox: free tag for both roles, payout line for listeners.
 */
export const FirstSessionFreeTxTag = ({ transaction, role, compact = false }) => {
  const txId = transaction?.id?.uuid;
  const free = isFirstSessionFreeTx(transaction);
  const [facts, setFacts] = useState(null);
  useEffect(() => {
    if (!free || !txId) return undefined;
    let live = true;
    Promise.resolve()
      .then(() => fetchFirstFreeTx(txId))
      .then(res => {
        if (live) setFacts(res?.data || null);
      })
      .catch(() => null);
    return () => {
      live = false;
    };
  }, [free, txId]);
  if (!free) {
    return null;
  }
  const payoutLabel = money(facts?.payoutSubunits) || listenerPayoutLabelOf(transaction) || null;
  const isListenerRole = role === 'provider' || role === 'listener';
  const sweet = isSweetTreatTx(transaction) || facts?.funding === 'listener';
  if (isListenerRole && sweet) {
    // Sweet Treat (Sam, Oct 19 2026 5:46 AM): the listener must always see it's unpaid.
    return <SweetTreatListenerLabel compact={compact} />;
  }
  if (isListenerRole) {
    // Platform-paid free session: the listener sees a normal paid session.
    if (compact) {
      return null;
    }
    return <ListenerNormalPayout transaction={transaction} facts={facts} payoutLabel={payoutLabel} />;
  }
  if (compact) {
    // Short line for the session join card (before / after the call).
    return (
      <div className={css.txTagCompact} data-testid="first-free-tx-compact">
        <span className={css.pill}>{sweet ? SWEET_TREAT_TALKER_LABEL : 'Free session'}</span>
        <span className={css.txTagCompactText}>$0.00 to you · 15 minutes</span>
      </div>
    );
  }
  return (
    <div className={css.txTag} data-testid="first-free-tx-talker">
      <span className={css.pill}>{sweet ? SWEET_TREAT_TALKER_LABEL : 'Free session'}</span>
      <p className={css.txTagTitle}>
        {sweet ? 'Free (Sweet Treat), $0.00. You weren’t charged.' : "Free session, $0.00. You weren't charged."}
      </p>
      <p className={css.txTagText}>
        Your free session is 15 minutes and can't be extended. If you don't join, your free
        session is used up.{' '}
        <NamedLink name="SearchPage" className={css.blueLink}>
          Find another listener
        </NamedLink>
      </p>
    </div>
  );
};

const FORMAT_NAMES = { chat: 'Chat', voice: 'Voice call', video: 'Video call' };

/** "Sweet Treat · Free session (unpaid)": every listener-side view of a Sweet Treat booking. */
export const SweetTreatListenerLabel = ({ compact = false, className }) => (
  <div
    className={classNames(compact ? css.sweetLabelCompact : css.sweetLabel, className)}
    data-testid="sweet-treat-listener-label"
    role="note"
  >
    <span className={css.sweetLabelPill}>{SWEET_TREAT_LISTENER_LABEL}</span>
    {compact ? null : (
      <span className={css.sweetLabelText}>
        This is the talker’s free first session. You’re not paid for it — thank you for
        giving a Sweet Treat.
      </span>
    )}
  </div>
);
const classNames = (...xs) => xs.filter(Boolean).join(' ');

/** Listener's view of a promo booking: identical to a normal paid session's payout. */
const ListenerNormalPayout = ({ transaction, facts, payoutLabel }) => {
  const pd = transaction?.attributes?.protectedData || {};
  const fmt = facts?.sessionFormat || pd.sessionFormat || 'voice';
  const priceSub = Number.isFinite(pd.talkerPriceSubunits) ? pd.talkerPriceSubunits : null;
  const payoutSub = Number.isFinite(facts?.payoutSubunits)
    ? facts.payoutSubunits
    : Number.isFinite(pd.listenerPayoutSubunits)
    ? pd.listenerPayoutSubunits
    : null;
  const feeSub = priceSub != null && payoutSub != null ? priceSub - payoutSub : null;
  const noShow = facts?.outcome === 'talker_no_show';
  return (
    <div className={css.normalPayout} data-testid="listener-normal-payout">
      <div className={css.lineRow}>
        <span>{FORMAT_NAMES[fmt] || 'Session'} · 15 minutes</span>
        <span className={css.lineAmount}>{money(priceSub) || ''}</span>
      </div>
      {feeSub != null ? (
        <div className={`${css.lineRow} ${css.lineRowMuted}`}>
          <span>Service fee</span>
          <span className={css.lineAmount}>−{money(feeSub)}</span>
        </div>
      ) : null}
      <div className={css.totalRow}>
        <span>{noShow ? "Talker didn't join: no payout" : 'You\'ll earn'}</span>
        <span>{noShow ? '$0.00' : payoutLabel || money(payoutSub) || ''}</span>
      </div>
    </div>
  );
};

/** Invited talker who earned the reward before Oct 19: small dashboard note. */
export const FreeSessionUnlockNote = () => {
  const { status } = useFirstSessionFree();
  const t = status?.role === 'talker' ? status.talker : null;
  if (!t || !(t.reason === 'locked' || t.reason === 'too_early')) {
    return null;
  }
  const f = t.reward && FORMAT_WORDS[t.reward.format];
  return (
    <div className={css.unlockNote} data-testid="free-session-unlock-note">
      <span className={css.pill}>Free session earned</span>
      <span className={css.unlockText}>Your free {f ? `${f} ` : ''}session unlocks Oct 19. Use it by Oct 31.</span>
    </div>
  );
};

/** Listener dashboard: the Sweet Treat switch + weekly limit (Oct 19 2026). */
export const ListenerFreeSessionsNote = () => <ListenerFreeSessionSwitch placement="dashboard" />;

export const LISTENER_SWITCH_LABEL = 'Sweet Treat: free first session';
export const LISTENER_SWITCH_HELP =
  'You’re not paid for Sweet Treat sessions — you give the 15 minutes to get new talkers and reviews. Paid sessions still pay your rate.';
export const LISTENER_SETUP_NOTE =
  'On by default. Turn it off now or anytime from your dashboard.';

const ET = 'America/New_York';
const etDateKey = ms => new Date(ms).toLocaleDateString('en-CA', { timeZone: ET });
const etHour = ms =>
  Number(new Intl.DateTimeFormat('en-US', { timeZone: ET, hour: '2-digit', hourCycle: 'h23' }).format(new Date(ms)));
/** "at midnight ET tonight" when the gate is the coming ET midnight, else "on Tue, Oct 20". */
export const talkerStartWords = (startMs, nowMs = Date.now()) => {
  const midnight = etHour(startMs) === 0 && new Date(startMs).getUTCMinutes() === 0;
  if (midnight && etDateKey(startMs - 60 * 1000) === etDateKey(nowMs)) return 'at midnight ET tonight';
  const day = new Date(startMs).toLocaleDateString('en-US', { timeZone: ET, weekday: 'short', month: 'short', day: 'numeric' });
  const time = new Date(startMs).toLocaleTimeString('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit' });
  return midnight ? `at midnight ET on ${day}` : `on ${day} at ${time} ET`;
};

const etDay = ms =>
  new Date(ms).toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

const postListenerSwitch = body =>
  window
    .fetch('/api/promo/first-free/listener', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
    .then(r => {
      if (!r.ok) throw new Error('save failed');
      offersCache = { at: 0, data: null, promise: null };
      return r.json();
    });

/**
 * Listener Sweet Treat switch (setup + dashboard). On by default (opt-out).
 * Setup: pre-on with an unpaid note; saving there starts it right away.
 * Dashboard: switch, weekly limit (1/3/5/10, default 3), and "Starts [date]" for
 * existing listeners before their start date.
 */
export const ListenerFreeSessionSwitch = ({ placement = 'dashboard', className, previewView = null }) => {
  const { status } = useFirstSessionFree();
  const fromServer = previewView || status?.listener || null;
  const [view, setView] = useState(fromServer);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const setupSaved = React.useRef(false);
  useEffect(() => {
    if (fromServer) setView(fromServer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(fromServer || {})]);
  // Listener and 'both' accounts: the server sends `listener` even before talkers see anything.
  const isListener = !!(previewView || status?.listener);
  // Setup: record the listener (starts now, on by default) the first time they see it.
  useEffect(() => {
    if (placement !== 'setup' || previewView || !isListener || setupSaved.current) return;
    if (view && view.hasRecord) return;
    setupSaved.current = true;
    postListenerSwitch({ offer: view ? view.offering !== false : true, setup: true })
      .then(j => j?.data && setView(j.data))
      .catch(() => null);
  }, [placement, isListener, view, previewView]);
  if (!isListener) {
    return null;
  }
  const v = view || {};
  const on = v.offering !== false;
  const cap = v.weeklyCap || 3;
  const capOptions = v.weeklyCapOptions || [1, 3, 5, 10];
  const justOptedOut =
    typeof window !== 'undefined' && /freeSessionOptOut=done/.test(window.location?.search || '');
  const save = body => {
    const prev = view;
    setView({ ...(view || {}), ...('offer' in body ? { offering: body.offer } : {}), ...(body.weeklyCap ? { weeklyCap: body.weeklyCap } : {}) });
    setSaving(true);
    setError(false);
    if (previewView) {
      setSaving(false);
      return;
    }
    postListenerSwitch({ ...body, ...(placement === 'setup' ? { setup: true } : {}) })
      .then(j => j?.data && setView(j.data))
      .catch(() => {
        setView(prev);
        setError(true);
      })
      .finally(() => setSaving(false));
  };
  const talkerStartMs = Number(v.talkerStartsAt);
  const beforeTalkerStart = Number.isFinite(talkerStartMs) && talkerStartMs > Date.now();
  // Before launch (talker gate not passed, or master switch off): same pill + line as the early ship.
  const preLaunch = beforeTalkerStart || v.talkersSeeIt === false;
  const startsLater = !preLaunch && on && Number.isFinite(v.startsAt) && v.startsAt > Date.now();
  const noStartYet = on && v.state === 'not_started' && !Number.isFinite(v.startsAt);
  const stateLine = !on
    ? 'Off: talkers pay your normal price. Turn it back on anytime.'
    : preLaunch
    ? `On, up to ${cap} a week. You can change this anytime.`
    : v.programOn === false
    ? 'On. Sweet Treat hasn’t started yet. We’ll email you before it begins.'
    : startsLater
    ? `Starts ${etDay(v.startsAt)}. Until then, talkers pay your normal price.`
    : noStartYet
    ? 'On. We’ll email you the start date before it begins.'
    : v.state === 'cap'
    ? `On. You’ve given ${v.thisWeek} of ${cap} Sweet Treats this week, so your badge is hidden until Monday.`
    : `On: your profile shows the “Sweet Treat” badge. Up to ${cap} a week. This week: ${v.thisWeek || 0} of ${cap}.`;
  return (
    <section
      className={className ? `${css.switchCard} ${className}` : css.switchCard}
      data-testid={`listener-free-switch-${placement}`}
    >
      {justOptedOut && placement === 'dashboard' ? (
        <p className={css.switchNotice} role="status">
          Done. You’ve turned off the Sweet Treat. You can turn it back on below anytime.
        </p>
      ) : null}
      {beforeTalkerStart ? (
        <p className={css.switchStarts} data-testid="listener-free-starts">
          Talkers start seeing Sweet Treat {talkerStartWords(talkerStartMs)}.
        </p>
      ) : v.talkersSeeIt === false ? (
        <p className={css.switchStarts} data-testid="listener-free-starts">
          Talkers don’t see Sweet Treat yet. We’ll email you when it starts.
        </p>
      ) : startsLater && placement === 'dashboard' ? (
        <p className={css.switchStarts} data-testid="listener-free-starts">
          Starts {etDay(v.startsAt)}
        </p>
      ) : null}
      <div className={css.switchRow}>
        <div className={css.switchText}>
          <span className={css.switchLabel} id={`ff-switch-${placement}`}>
            {LISTENER_SWITCH_LABEL}
          </span>
          <p className={css.switchHelp}>{LISTENER_SWITCH_HELP}</p>
          {placement === 'setup' ? (
            <p className={css.switchUnpaid} data-testid="listener-free-setup-note">
              {LISTENER_SETUP_NOTE}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-labelledby={`ff-switch-${placement}`}
          className={on ? `${css.toggle} ${css.toggleOn}` : css.toggle}
          onClick={() => save({ offer: !on })}
          disabled={saving}
          data-testid="listener-free-switch-toggle"
        >
          <span className={css.toggleKnob} />
        </button>
      </div>
      {on ? (
        <div className={css.capRow}>
          <label htmlFor={`ff-cap-${placement}`} className={css.capLabel}>
            Weekly limit (most Sweet Treats per week)
          </label>
          <select
            id={`ff-cap-${placement}`}
            className={css.capSelect}
            value={cap}
            disabled={saving}
            onChange={e => save({ weeklyCap: Number(e.target.value) })}
            data-testid="listener-free-cap"
          >
            {capOptions.map(n => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <p className={css.switchState} data-testid="listener-free-state">
        {stateLine}
        {error ? ' Could not save. Please try again.' : ''}
      </p>
      {placement === 'dashboard' ? (
        <p className={css.switchFaq}>
          Cancelled sessions and sessions where you couldn’t join don’t count toward your weekly
          limit.{' '}
          <NamedLink name="FAQPage" to={{ hash: 'sweetTreatListener' }} className={css.blueLink}>
            How Sweet Treat works
          </NamedLink>
        </p>
      ) : null}
    </section>
  );
};
export const ListenerFreeSessionSwitchRemoved = ListenerFreeSessionSwitch;

/**
 * Listing booking panel: tells an eligible talker which formats are free with
 * this listener. Returns { free: bool, node } so the panel can mark cards.
 */
export const useFreeForListener = (listenerId, currentUser) => {
  const { eligible, needsVerify, live, status } = useFirstSessionFree(currentUser);
  const reward = rewardOf(status);
  const [full, setFull] = useState(false);
  useEffect(() => {
    if (!eligible || !listenerId) return undefined;
    let ok = true;
    Promise.resolve()
      .then(() => fetchFirstFreeStatus(listenerId))
      .then(res => {
        if (ok) setFull(!!res?.data?.talker?.listenerFull);
      })
      .catch(() => null);
    return () => {
      ok = false;
    };
  }, [eligible, listenerId]);
  return {
    free: eligible && !full,
    listenerFull: eligible && full,
    needsVerify,
    live,
    reward,
    rewardSubunits: reward ? reward.priceSubunits : isListenerFundedStatus(status) ? 3000 : 0,
  };
};

export const FreeFormatPrice = ({ format, normalLabel, reward }) =>
  !FREE_FORMATS.includes(format) || (reward && !formatWithinReward(format, reward)) ? (
    <span className={css.formatPriceWrap}>
      <span>{normalLabel}</span>
      <span className={css.formatNotFree}>Not free</span>
    </span>
  ) : (
    <span className={css.formatPriceWrap}>
      <span className={css.formatFree}>Free</span>
    </span>
  );

const rewardWords = reward => {
  const f = reward && FORMAT_WORDS[reward.format];
  return f === 'video' ? 'video, voice or chat' : f === 'voice' ? 'voice or chat' : f === 'chat' ? 'chat' : 'chat, voice or video';
};

export const FirstSessionFreePanelNote = ({ free, listenerFull, needsVerify, reward }) => {
  if (free) {
    return (
      <div className={css.panelNote} data-testid="first-free-panel-note">
        <FirstSessionFreeBadge label={reward ? 'Free session' : 'Sweet Treat'} />
        <p className={css.panelNoteText}>
          {reward
            ? `You earned a free 15-minute ${rewardWords(reward)} session. The payment page shows $0.00.`
            : 'Your Sweet Treat: your first 15-minute session is free with this listener, in any format they offer. The payment page shows $0.00.'}
        </p>
      </div>
    );
  }
  if (listenerFull) {
    return (
      <div className={css.panelNote} data-testid="first-free-panel-full">
        <p className={css.panelNoteText}>
          This listener isn’t offering the Sweet Treat right now.{' '}
          <NamedLink name="SearchPage" to={{ search: '?sweetTreat=1' }} className={css.blueLink}>
            Find a listener with the Sweet Treat badge
          </NamedLink>{' '}
          for your free first session, or book here at the normal price.
        </p>
      </div>
    );
  }
  return null;
};

/** True when a logged-in talker can no longer use the free session (used it or ever paid). */
export const useFreeSessionIneligible = () => {
  const { status } = useFirstSessionFree();
  const t = status?.role === 'talker' ? status.talker : null;
  return !!(t && (t.claim || t.reason === 'already_claimed' || t.reason === 'not_first'));
};

/** Listener card / profile badge: guests and eligible talkers, while live. */
export const ListingFreeBadge = ({ className, listenerId }) => {
  const offers = usePublicOffers();
  const { status } = useFirstSessionFree();
  if (!listenerOffersFree(offers, listenerId)) {
    return null;
  }
  // A talker who already used (or can't use) their free session doesn't need the badge.
  const t = status?.role === 'talker' ? status.talker : null;
  // Sam, Oct 21 (final): talkers who can't use it (used it or ever paid) don't see it at all.
  const used = !!(t && (t.claim || t.reason === 'already_claimed' || t.reason === 'not_first'));
  if (used) return null;
  return (
    <FirstSessionFreeBadge
      className={used ? `${className || ''} ${css.badgeUsed}`.trim() : className}
      title={used ? 'This listener offers a free first session. You have already used yours.' : 'With this listener, your first 15 minutes are free.'}
      data-free-used={used ? 'true' : 'false'}
      label="🎁 First 15 minutes free"
      noIcon
      data-sweet-badge="true"
      data-testid="first-session-free-badge"
    />
  );
};

/** FAQ rows (plan §7, no volunteer item). Shown on /faq only while the promo is live. */
// Sweet Treat FAQ rows live in FAQPage (FAQ_ITEM_DEFS, gated on the offer being live).
export const FREE_FAQ_ITEMS = [];

export default FirstSessionFreePopup;


/**
 * Talker funnel (Oct 21 2026, TEST): a plain, prominent "Your first 15 minutes
 * are free" line under the price on listener profiles. Same rule as the badge.
 */
export const FirstFifteenFreeLine = ({ listenerId, className }) => {
  const offers = usePublicOffers();
  const { status } = useFirstSessionFree();
  if (!listenerOffersFree(offers, listenerId)) return null;
  const t = status?.role === 'talker' ? status.talker : null;
  if (t && (t.claim || t.reason === 'already_claimed' || t.reason === 'not_first')) return null;
  return (
    <p className={[css.fifteenFree, className].filter(Boolean).join(' ')} data-testid="first-fifteen-free">
      🎁 Your first 15 minutes are free
    </p>
  );
};

/**
 * Sam 2:50 Oct 23: every free-session CTA hides for signed-in people who already
 * used theirs (or can't use one). Guests always see them.
 */
export const useShowFreeCtas = () => {
  const { status, eligible, needsVerify, guest } = useFirstSessionFree();
  const growth = useGrowthStatus();
  if (guest) return true;
  if ((growth?.credits || []).some(c => c.from === 'refund')) return true;
  if (!status) return false;
  return !!(eligible || needsVerify);
};

// Oct 24 (TEST): checkout line explaining why there is no free session (server-sourced).
export const FreeRefusedWhy = ({ className }) => {
  const { refusedReason } = useFirstSessionFree();
  if (!refusedReason) return null;
  const text = ['already_claimed', 'already_booked'].includes(refusedReason)
    ? "Why no free session? You've already used your free first session."
    : 'Why no free session? Free first sessions are one per person and one per card, and your card or ID has already had one.';
  return (
    <p className={className} data-testid="free-refused-why">
      {text}
    </p>
  );
};

/**
 * Sam Oct 30 1:46 PM: no $ amounts on profiles/cards/CTAs until the talker has COMPLETED
 * their free first session (claim status 'used') or the server says they had one before.
 * Guests and talkers with an eligible / pending-verify / reserved free session see FREE only.
 */
export const useHidePrices = () => {
  const { status, eligible, needsVerify, guest } = useFirstSessionFree();
  const growth = useGrowthStatus();
  if (guest) return true;
  // Sweet Guarantee (Oct 30): FREE UI until the make-good session is completed.
  if ((growth?.credits || []).some(c => c.from === 'refund')) return true;
  if (!status || status.role !== 'talker') return false;
  const t = status.talker || {};
  if (t.claim) return t.claim.status !== 'used';
  if (t.reason === 'already_claimed' || t.reason === 'not_first') return false;
  return !!(eligible || needsVerify);
};
